import { mapPagination } from '@/shared/mappers';
import { DataWithPagination, Page } from '@/shared/types';
import { getFullString } from '@/utils';
import {
  BusinessAreaEmployee,
  CompanyEmployee,
  CurrentUser,
  OrganizationAdmin,
  OrganizationEmployee,
  TaskUser,
  TenantUser,
  UserBaseInfo,
  UserDetails,
  UserItem,
} from '../domain';
import { Role } from '../domain/role';
import { UserRateModel } from '../domain/user-rate-model';
import {
  ApiResponseUserRates,
  BusinessAreaEmployeeJson,
  CompanyEmployeeJson,
  CurrentUserJson,
  OrganizationAdminJson,
  OrganizationEmployeeJson,
  PagedUserRates,
  RoleJson,
  TaskUserJson,
  TenantUserJson,
  UserBaseInfoJson,
  UserDetailsJson,
  UserItemJson,
} from '../types';
import { UserRate } from '../types/user-rates';

export const mapCurrentUser = (user: CurrentUserJson): CurrentUser => ({
  id: user.id ?? 0,
  firstName: user.firstName ?? '',
  lastName: user.lastName ?? '',
  access: user.access ?? false,
  canDelete: user.canDelete ?? false,
  email: user.email ?? '',
  fullName: user.fullName ?? '',
  status: user.status ?? false,
  profileImageFileId: user.profileImageFileId,
  organizations: user.organizations ?? [],
  isAccountAdmin: user.isAccountAdmin ?? false,
  isPlatformAdmin: user.isPlatformAdmin ?? false,
});

export const mapUserDetails = (userDetails: UserDetailsJson): UserDetails => ({
  id: userDetails.id ?? 0,
  firstName: userDetails.firstName ?? '',
  lastName: userDetails.lastName ?? '',
  name: getFullString(userDetails.firstName ?? '', userDetails.lastName ?? ''),
  access: userDetails.access ?? false,
  status: userDetails.status ?? false,
  email: userDetails.email ?? '',
  hourRate: userDetails.hourRate ?? 0,
  profileImageFileId: userDetails.profileImageFileId,
  adminPrivilegesCount: userDetails.adminPrivilegesCount ?? 0,
  businessAreasCount: userDetails.businessAreasCount ?? 0,
  processesCount: userDetails.processesCount ?? 0,
  tasksCount: userDetails.tasksCount ?? 0,
});

export const mapUserItemPage = (userItems: Page<UserItemJson>) => ({
  items: userItems.items.map<UserItem>((user) => ({
    id: user.id ?? 0,
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    access: user.access ?? false,
    canDelete: user.canDelete ?? false,
    email: user.email ?? '',
    fullName: user.fullName ?? '',
    status: user.status ?? false,
    profileImageFileId: user.profileImageFileId,
  })),
  ...mapPagination(userItems),
});

export const mapUsersBaseInfo = (users: UserBaseInfoJson[]): UserBaseInfo[] =>
  users.map((u) => ({
    id: u.id ?? 0,
    firstName: u.firstName ?? '',
    lastName: u.lastName ?? '',
    email: u.email ?? '',
    status: u.status ?? false,
  }));

export const mapRoleJsonToRole = (data: RoleJson): Role => ({
  id: data.id ?? 0,
  name: data.name ?? '',
});

export const rolesMapper = (data: Array<RoleJson>): Array<Role> =>
  data.map(mapRoleJsonToRole);

export const mapUserRateJsonToUserModel = (
  userRateJson: UserRate,
): UserRateModel => ({
  id: userRateJson.id ?? 0,
  value: userRateJson.value ?? 0,
  startDate: userRateJson.startDate && new Date(userRateJson.startDate),
  endDate: userRateJson.endDate && new Date(userRateJson.endDate),
  jiraAccountId: userRateJson.jiraAccountId ?? '',
  updatedAt: userRateJson.updatedAt && new Date(userRateJson.updatedAt),
  createdAt: userRateJson.createdAt && new Date(userRateJson.createdAt),
});

export const userRatesMapper = (
  usersJson: Array<UserRate>,
): Array<UserRateModel> => usersJson.map(mapUserRateJsonToUserModel);

export const userRatesWithPaginationMapper = (
  usersJson: ApiResponseUserRates<PagedUserRates>,
) => {
  const { data } = usersJson;
  return {
    items: userRatesMapper(data.hourlyRates.items),
    ...mapPagination(data.hourlyRates),
  };
};

export const mapOrganizationEmployeesPage = (
  page: Page<OrganizationEmployeeJson>,
): DataWithPagination<OrganizationEmployee[]> => {
  return {
    items: page.items.map<OrganizationEmployee>((employee) => ({
      id: employee.id ?? 0,
      fullName: employee.fullName ?? '',
      email: employee.email ?? '',
      access: employee.access ?? false,
      status: employee.status ?? false,
      profileImageFileId: employee.profileImageFileId,
      roleName: employee.roleName ?? '',
      roleEntity: employee.roleEntity,
    })),
    ...mapPagination(page),
  };
};

export const mapOrganizationAdminsPage = (
  page: Page<OrganizationAdminJson>,
): DataWithPagination<OrganizationAdmin[]> => {
  return {
    items: page.items.map<OrganizationAdmin>((admin) => ({
      id: admin.id ?? 0,
      fullName: admin.fullName ?? '',
      access: admin.access ?? false,
      email: admin.email ?? '',
      status: admin.status ?? false,
      profileImageFileId: admin.profileImageFileId,
    })),
    ...mapPagination(page),
  };
};

export const mapCompanyEmployeesPage = (
  page: Page<CompanyEmployeeJson>,
): DataWithPagination<CompanyEmployee[]> => {
  return {
    items: page.items.map<CompanyEmployee>((employee) => ({
      id: employee.id ?? 0,
      fullName: employee.fullName ?? '',
      email: employee.email ?? '',
      access: employee.access ?? false,
      status: employee.status ?? false,
      profileImageFileId: employee.profileImageFileId,
      roleName: employee.roleName ?? '',
      roleEntity: employee.roleEntity,
    })),
    ...mapPagination(page),
  };
};

export const mapBusinessAreEmployeesPage = (
  page: Page<BusinessAreaEmployeeJson>,
): DataWithPagination<BusinessAreaEmployee[]> => ({
  items: page.items.map<BusinessAreaEmployee>((employee) => ({
    id: employee.id ?? 0,
    fullName: employee.fullName ?? '',
    email: employee.email ?? '',
    access: employee.access ?? false,
    status: employee.status ?? false,
    profileImageFileId: employee.profileImageFileId,
    roleId: employee.roleId ?? 0,
    roleName: employee.roleName ?? '',
  })),
  ...mapPagination(page),
});

export const mapTaskUsersPage = (
  page: Page<TaskUserJson>,
): DataWithPagination<TaskUser[]> => {
  return {
    items: page.items.map<TaskUser>((taskUser) => ({
      id: taskUser.id ?? 0,
      fullName: taskUser.fullName ?? '',
      email: taskUser.email ?? '',
      access: taskUser.access ?? false,
      status: taskUser.status ?? false,
      profileImageFileId: taskUser.profileImageFileId,
      roleName: taskUser.roleName ?? '',
      roleId: taskUser.roleId ?? 0,
    })),
    ...mapPagination(page),
  };
};

export const mapTenantUsersPage = (
  page: Page<TenantUserJson>,
): DataWithPagination<TenantUser[]> => ({
  items: page.items.map<TenantUser>((i) => ({
    id: i.id ?? 0,
    firstName: i.firstName ?? '',
    lastName: i.lastName ?? '',
    fullName: i.fullName ?? '',
    email: i.email ?? '',
    access: i.access ?? false,
    status: i.status ?? false,
    accountAdmin: i.accountAdmin ?? false,

    profileImageFileId: i.profileImageFileId,
  })),
  ...mapPagination(page),
});
